import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _merge from 'lodash/merge';
import set from 'immutable-set';

import { LAYOUT_ENTITIES, LAYOUT_FRONTSPACES } from '@bedrock/fido-sdk';
import {
  FETCH_NAVIGATION_SUCCESS,
  SET_CURRENT_ENTITY_ID,
  GENERATE_BLOCK_PLACEHOLDERS,
  FETCH_BLOCK,
  SET_CURRENT_SECTION_CODE,
  SET_REQUESTED_ENTITY,
  SET_CURRENT_ENTITY_TYPE,
  SET_CURRENT_ENTITY,
  SET_CONTENT_TEMPLATES_OPTIONS,
  CLEAR_CACHE,
  INVALIDATE_ITEM_CACHE,
  createSuccessActionType,
  REPLACE_BLOCK,
  SET_NAVIGATION_ALLOWED,
} from './layout.actions';
import { isValidItemPosition, pagesToLoad } from './layout.utils';
import { applyLayoutCacheLimit, getBlockInfo, makeLayoutCacheKey, touchLayoutCache } from './layout.reducer.utils';
import { PAGE_STATE, PLACEHOLDER } from '../../modules/tornado/utils/virtualization';

export const initialState = {
  navigation: null,
  navigationAllowed: false,
  currentService: null,
  currentSectionCode: 'main',
  currentEntityType: null,
  currentEntityId: null,
  requestedEntityId: null,
  requestedEntityType: null,
  // Most recently used last, see applyLayoutCacheLimit
  cacheOrder: [],
};

export default (state = initialState, action) => {
  const FETCHED_LAYOUT_ACTION = createSuccessActionType(action.entity);

  switch (action.type) {
    case FETCHED_LAYOUT_ACTION: {
      const { entity, id, data } = action;
      const { currentSectionCode } = state;
      const isBookmarks = entity === LAYOUT_ENTITIES.frontspace && id === LAYOUT_FRONTSPACES.bookmarks;
      const oldBlocks = _get(state, [currentSectionCode, entity, id, 'blocks'], []);

      data.blocks = data.blocks.map(block => {
        const { items, pagination } = block.content;
        block.pages = pagesToLoad(pagination.totalItems, pagination.itemsPerPage, items);

        if (block.alternativeContent && block.alternativeContent.concurrentBlocks) {
          block.alternativeContent.concurrentBlocks = block.alternativeContent.concurrentBlocks.map(
            (concurrentBlock, index) => {
              const { pagination } = concurrentBlock.content;
              concurrentBlock.pages = pagesToLoad(pagination.totalItems, pagination.itemsPerPage, index ? [] : items);

              return concurrentBlock;
            },
          );
        }

        return block;
      });

      if (oldBlocks.length && oldBlocks.length === data.blocks.length) {
        data.blocks = oldBlocks.map((oldBlock, index) => {
          const newBlock = data.blocks[index];
          const { featureId } = newBlock;
          const isUserSpecificBlock =
            isBookmarks || featureId === 'bookmarks_by_user' || featureId === 'playback_by_user';

          // When a block was paginated, so there is more data in the cache
          if (oldBlock.content.items.length !== newBlock.content.items.length) {
            // User-specific blocks has correct pagination info in the new layout
            const count = isUserSpecificBlock
              ? newBlock.content.pagination.totalItems
              : Math.max(oldBlock.content.items.length, newBlock.content.items.length);
            const items = [];
            for (let i = 0; i < count; i += 1) {
              // Placing the new item on top of the old one, or a placeholder if there is no item at this position
              items.push(newBlock.content.items[i] || oldBlock.content.items[i] || PLACEHOLDER);
            }

            newBlock.content.items = items;
            if (!isUserSpecificBlock) {
              newBlock.content.pagination = oldBlock.content.pagination;
            }
          }

          const newAlt = newBlock.alternativeContent;
          const oldAlt = oldBlock.alternativeContent;

          if (newAlt && oldAlt) {
            for (let i = 0; i < newAlt.concurrentBlocks.length; i += 1) {
              if (oldAlt.concurrentBlocks[i]) {
                newAlt.concurrentBlocks[i] = {
                  ...oldAlt.concurrentBlocks[i],
                  pages: newAlt.concurrentBlocks[i].pages,
                };
              }
            }

            if (newAlt.concurrentBlocks.length) {
              const { content } = newAlt.concurrentBlocks[0];

              if (content.items) {
                content.items.splice(0, newBlock.content.items.length, ...newBlock.content.items);
              } else {
                content.items = newBlock.content.items;
              }
            }
          }

          return newBlock;
        });
      }

      const nextState = {
        ...state,
        // Automatically allowing the Sidenav to appear once we have a valid layout
        navigationAllowed: true,
        currentService: entity === 'service' ? id : state.currentService,
        [currentSectionCode]: {
          ...state[currentSectionCode],
          [entity]: {
            ...(state[currentSectionCode] && state[currentSectionCode][entity]),
            [id]: data,
          },
        },
      };

      // Writing a layout is the only point one enters the cache, so it is also where the cache is trimmed
      return applyLayoutCacheLimit(
        nextState,
        touchLayoutCache(state.cacheOrder, makeLayoutCacheKey(currentSectionCode, entity, id)),
      );
    }

    case FETCH_BLOCK: {
      const { blockId, items: newItems, pageIndex, parentId, totalItems: newTotalItems } = action;
      const blockInfo = getBlockInfo(state, blockId, parentId);
      if (!blockInfo) {
        return state;
      }

      const [path, block] = blockInfo;
      const { items: currentItems, pagination } = block.content;
      const lastLoadedPage = block.lastLoadedPage || 0;
      // Pages index starts from 1, so transforming it to zero-based index
      const page = pageIndex - 1;
      // Bigger page index means more precise knowledge about the total items count
      const totalItems = (page > lastLoadedPage && newTotalItems) || pagination.totalItems;
      const start = page * pagination.itemsPerPage;
      const end = start + newItems.length;

      const items = [];
      // NOTE: considering placeholders were made before this action so the current items list already holds placeholders for a new page
      for (let i = 0; i < currentItems.length; i += 1) {
        items.push(i >= start && i < end ? newItems[i - start] : currentItems[i]);
      }

      return set(state, path.join('.'), {
        ...block,
        lastLoadedPage: Math.max(page, lastLoadedPage),
        pages: {
          ...pagesToLoad(totalItems, pagination.itemsPerPage, items),
          ...block.pages,
          [page]: PAGE_STATE.LOADED,
        },
        content: {
          ...block.content,
          items,
          pagination: {
            ...block.content.pagination,
            totalItems,
          },
        },
      });
    }

    case GENERATE_BLOCK_PLACEHOLDERS: {
      const blockInfo = getBlockInfo(state, action.blockId, action.parentId);
      if (!blockInfo) {
        return state;
      }

      const [path, block] = blockInfo;
      const { items, pagination } = block.content;
      const start = Math.min(items.length, action.start);
      const count = Math.min(
        pagination.totalItems - items.length,
        Math.max(action.start - items.length, 0) + pagination.itemsPerPage,
      );
      const placeholders = [];
      for (let i = 0; i < count; i += 1) {
        if (!items[start + i]) {
          placeholders.push(PLACEHOLDER);
        }
      }
      // NOTE: the page passed via the action is 1-based (as it should be forwarded to BFF)
      const pageIndex = action.page - 1;

      return set(state, path.join('.'), {
        ...block,
        content: { ...block.content, items: items.concat(placeholders) },
        pages: { ...block.pages, [pageIndex]: PAGE_STATE.LOADING },
      });
    }

    case CLEAR_CACHE: {
      const { entityType, entityId, sectionCode } = action;
      const path = [sectionCode, entityType, entityId].filter(Boolean);

      // NOTE: clear the entire cache if no path specified
      if (!path.length) {
        return {
          ...state,
          main: {},
          kids: {},
          cacheOrder: [],
        };
      }

      const pathToOmit = path.pop();

      return {
        contentTemplatesOptions: state.contentTemplatesOptions,
        ...set(state, path.join('.'), _omit(_get(state, path.join('.')), pathToOmit)),
      };
    }

    case INVALIDATE_ITEM_CACHE: {
      const { itemIndex, blockIndex } = action;

      if (!isValidItemPosition(blockIndex, itemIndex) || !action.item) {
        return state;
      }

      const { requestedEntityId, requestedEntityType, currentSectionCode } = state;
      const path = `${currentSectionCode}.${requestedEntityType}.${requestedEntityId}.blocks[${blockIndex}].content.items[${itemIndex}]`;
      const item = _get(state, path);

      return item ? set(state, path, _merge({}, item, action.item)) : state;
    }

    case REPLACE_BLOCK: {
      const { blockId, items: newItems, pagination: newPagination, date: newDate } = action;
      const { requestedEntityId, requestedEntityType, currentSectionCode } = state;
      const blocks = _get(state, [currentSectionCode, requestedEntityType, requestedEntityId, 'blocks'], []);
      const blockIndex = blocks.findIndex(({ id }) => id === blockId);
      const path = [currentSectionCode, requestedEntityType, requestedEntityId, `blocks[${blockIndex}]`].join('.');

      const currentState = _get(state, path, {});

      return set(state, path, {
        ...currentState,
        content: {
          ...currentState.content,
          items: newItems,
          date: newDate,
        },
        pages: pagesToLoad(newPagination.totalItems, newPagination.itemsPerPage, newItems),
      });
    }

    case FETCH_NAVIGATION_SUCCESS: {
      if (!action.navigation) return state;

      // NOTE: the section may have changed while the request was in flight
      return set(state, [action.sectionCode || state.currentSectionCode, 'navigation'], action.navigation);
    }

    case SET_CURRENT_ENTITY: {
      return { ...state, currentEntityType: action.entityType, currentEntityId: action.entityId };
    }

    case SET_CURRENT_ENTITY_TYPE: {
      return { ...state, currentEntityType: action.entityType };
    }

    case SET_CURRENT_ENTITY_ID: {
      return { ...state, currentEntityId: action.entityId };
    }

    case SET_CURRENT_SECTION_CODE: {
      return { ...state, currentSectionCode: action.sectionCode };
    }

    case SET_REQUESTED_ENTITY: {
      return {
        ...state,
        requestedEntityId: action.entityId,
        requestedEntityType: action.entityType,
      };
    }

    case SET_CONTENT_TEMPLATES_OPTIONS: {
      return {
        ...state,
        contentTemplatesOptions: action.contentTemplatesOptions,
      };
    }

    case SET_NAVIGATION_ALLOWED: {
      return {
        ...state,
        navigationAllowed: Boolean(action.isAllowed),
      };
    }

    default: {
      return state;
    }
  }
};
